import { ReactElement } from "react";
import { Button } from "@twilio-paste/core/button";
import { Box } from "@twilio-paste/core/box";
import { HelpText } from "@twilio-paste/core/help-text";

import {
  MessageBlockRole,
  MessageCompletionStatus,
  trackingDetails,
} from "@twiliointernal/e2-commons-shared-definitions";

import { useWiseOwlContext } from "../../../hooks";
import { types, copy } from "../definitions";

const RetryRegenerateRenderer = ({
  block,
}: types.MessageRendererProps): ReactElement => {
  const {
    chatId,
    loadingConfiguration: { submission },
    handlers: {
      handleMessageRetry,
      handleResponseRegeneration,
      handleTrackEvent,
    },
    applicationProps: { displayName, ingress },
  } = useWiseOwlContext();
  const { role, completionStatus, success, retryId, createChatOnRetry } =
    block.values;

  const isAiResponseError = completionStatus === MessageCompletionStatus.Error;
  const isMessageError =
    isAiResponseError ||
    (success === false && role === MessageBlockRole.User && retryId);
  const isRetryButtonDisabled = createChatOnRetry && submission;

  const onMessageRetry = (retryId: string) => {
    if (handleMessageRetry) {
      handleMessageRetry(retryId);
    }

    if (handleTrackEvent) {
      handleTrackEvent(
        trackingDetails.wiseOwl.buttonActionClicked(displayName, "Retry"),
        {
          chatId,
          messageId: retryId,
          ingress,
        }
      );
    }
  };

  const onResponseRegeneration = () => {
    const { id } = block.values;
    if (handleResponseRegeneration) {
      handleResponseRegeneration(id);
    }

    if (handleTrackEvent) {
      handleTrackEvent(
        trackingDetails.wiseOwl.buttonActionClicked(
          displayName,
          "Regeneration"
        ),
        {}
      );
    }
  };

  return (
    <Box>
      {isMessageError && (
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="flex-end"
          columnGap={"space20"}
        >
          <HelpText variant="error">
            {isAiResponseError
              ? copy.regenerateSendMessage
              : copy.retryCreateChat}
          </HelpText>
          <Button
            variant={"link"}
            disabled={isRetryButtonDisabled}
            onClick={() => {
              if (isAiResponseError) {
                onResponseRegeneration();
              }
              if (retryId) {
                onMessageRetry(retryId);
              }
            }}
          >
            {isAiResponseError
              ? copy.regenerateSendMessageLinkText
              : copy.retryCreateChatLinkText}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RetryRegenerateRenderer;
